import { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import {
  Alert,
  Box,
  BoxProps,
  DialogContent,
  Drawer,
  drawerClasses,
  IconButton,
  modalClasses,
  Stack,
  styled,
  tabsClasses,
  Tooltip,
  Typography,
} from '@mui/material';

import { HasPermission } from '@components/auth';
import { AvailabilityTag, Icon, Loading, Menu, Tabs } from '@components/base';
import { ProviderIcon } from '@components/content';
import ImageGroupDataProvider from '@hooks/imageHooks/useImageGroupData/provider';
import usePlaybackOptionQuery from '@hooks/queryHooks/usePlaybackOptionQuery';
import useTitleQuery from '@hooks/queryHooks/useTitleQuery';
import GenericDataContextProvider from '@hooks/useGenericDataContext/provider';
import useMetadataContext from '@hooks/useMetadataContext';
import { useProviderNames } from '@hooks/useProviderNames';
import useSubscriber from '@hooks/useSubscriber';
import { useIsNewIngest } from '@hooks/useTitlesDataSource';
import { getCurrentSearchLang } from '@models/CountriesAndLanguages';
import {
  DetailedPlaybackOption,
  DetailedTitle,
  PlaybackOptionDetailed,
  TTitleContext,
} from '@models/Title';
import ViewAs from '@modules/layouts/components/layoutPreview/ViewAs';
import useMasterIdManager from '@modules/masterIdManager/useMasterIdManager';
import { PlatformId, SourceId } from '@modules/simulations/types';
import { useAppSelector } from '@store/hooks';
import { getSimpleTitle } from '@store/titles/slice';
import { getAvailabilityWindow } from '@utils/availabilityUtils';
import { getTitleImageUrlFromProvider } from '@utils/imageUtils';

import { SectionHeading } from './Details/Section';
import Details from './Details';
import Episodes from './Episodes';
import HighlightedTypography from './HighlightedTypography';
import ImageTab from './ImageTab';
import Products from './Products';
import RawMetadataTabContent from './RawDataRevisions';
import SqlDataTree from './SqlDataTree';
import TitleDataTree from './TitleDataTree';

const StyledDrawer = styled(Drawer)`
  .paper {
    background-image: none;
    background: ${({ theme }) => theme.palette.background.default};
    display: flex;
    max-height: 100%;
    overflow-y: auto;
    width: 100%;

    ${props => props.theme.breakpoints.up('md')} {
      max-width: 1000px;
      width: 75%;
    }

    ${props => props.theme.breakpoints.up('lg')} {
      max-width: 1200px;
    }
  }

  overflow: auto;

  + .${drawerClasses.root} .${modalClasses.backdrop} {
    opacity: 0 !important;
  }
`;

const TitleBar = styled(Stack)`
  align-items: center;
  background: ${({ theme }) => theme.palette.background.default};
  display: flex;
  flex-wrap: nowrap;
  padding: ${({ theme }) => theme.spacing(1, 1, 0)};
  position: sticky;
  top: 0;
  width: 100%;

  ${props => props.theme.breakpoints.up('sm')} {
    padding: ${({ theme }) => theme.spacing(1.5, 1.5, 0)};
  }
`;

type TabKey = 'dataTree' | 'details' | 'episodes' | 'images' | 'products' | 'revisions' | 'sql';

const defaultTab: TabKey = 'details';

type TitleOptionsMenuProps = {
  title?: DetailedTitle;
};

const TitleOptionsMenu: FC<TitleOptionsMenuProps> = ({ title }) => {
  const { t } = useTranslation();
  const { openMergeTitlesView } = useMasterIdManager();

  return (
    <Menu>
      <Tooltip title={t('options')}>
        <span>
          <Menu.IconButton disabled={!title} size='small'>
            <Icon name='merge' />
          </Menu.IconButton>
        </span>
      </Tooltip>

      {title && (
        <Menu.Content>
          <Menu.Item onClick={() => openMergeTitlesView(title)}>{t('mergeTitle')}</Menu.Item>
        </Menu.Content>
      )}
    </Menu>
  );
};

type PlaybackOptionOptionsMenuProps = {
  playbackOption?: DetailedPlaybackOption;
  title?: DetailedTitle;
};

const PlaybackOptionOptionsMenu: FC<PlaybackOptionOptionsMenuProps> = ({
  playbackOption,
  title,
}) => {
  const { t } = useTranslation();
  const { handleSplitIntoNewTitle, openMovePlaybackOptionView } = useMasterIdManager();
  const disabled = !title || !playbackOption;

  return (
    <Menu>
      <Tooltip title={t('options')}>
        <span>
          <Menu.IconButton disabled={disabled} size='small'>
            <Icon name='move' />
          </Menu.IconButton>
        </span>
      </Tooltip>

      {!disabled && (
        <Menu.Content>
          <Menu.Item
            icon={<Icon name='move' />}
            onClick={() => openMovePlaybackOptionView(title, playbackOption)}
          >
            {t('playbackOptionAction.move')}
          </Menu.Item>
          <Menu.Item
            icon={<Icon name='add' />}
            onClick={() => handleSplitIntoNewTitle(title, playbackOption)}
          >
            {t('playbackOptionAction.split')}
          </Menu.Item>
        </Menu.Content>
      )}
    </Menu>
  );
};

const getLocalizedValue = <T,>(value?: Partial<Record<'da' | 'en' | 'no', T | null>> | null) => {
  const language = getCurrentSearchLang().toLowerCase() as 'da' | 'en' | 'no';
  return value?.[language] ?? value?.no ?? value?.en ?? value?.da;
};

const toDetailedPlaybackOption = (
  playbackOption: PlaybackOptionDetailed,
  summary: DetailedPlaybackOption
): DetailedPlaybackOption => ({
  ...summary,
  availableFrom: playbackOption.availableFrom ?? summary.availableFrom,
  availableTo: playbackOption.availableTo ?? summary.availableTo,
  data: summary.data && {
    ...summary.data,
    channelId: playbackOption.channelId ?? summary.data.channelId,
    channelName: playbackOption.channelName ?? summary.data.channelName,
    supportsCatchup: playbackOption.supportsCatchup ?? summary.data.supportsCatchup,
  },
  endTime: playbackOption.endTime ?? summary.endTime,
  id: playbackOption.id ?? summary.id,
  platformTitleId: playbackOption.platformTitleId ?? summary.platformTitleId,
  providerIdentifier: playbackOption.providerIdentifier ?? summary.providerIdentifier,
  providerTitleId: playbackOption.providerTitleId ?? summary.providerTitleId,
  providerType: playbackOption.serviceType ?? summary.providerType,
  serviceType: playbackOption.serviceType ?? summary.serviceType,
  sourceId: playbackOption.sourceId ?? summary.sourceId,
  startTime: playbackOption.startTime ?? summary.startTime,
});

const toDetailedTitle = (
  title: DetailedTitle | undefined,
  playbackOption: PlaybackOptionDetailed | undefined,
  summary: DetailedPlaybackOption | undefined
): (DetailedTitle & { mergeReason?: string }) | undefined => {
  if (!title || !playbackOption || !summary) return undefined;

  const price = playbackOption.price?.find(({ amount }) => amount != null)?.amount;

  return {
    ...title,
    ageRating: playbackOption.ageRating ?? title.ageRating,
    cast: playbackOption.cast ?? title.cast,
    country: playbackOption.country ?? title.country,
    descriptionLong: getLocalizedValue(playbackOption.descriptionLong) ?? title.descriptionLong,
    descriptionShort: getLocalizedValue(playbackOption.descriptionShort) ?? title.descriptionShort,
    duration: playbackOption.duration ?? title.duration,
    episode: playbackOption.episode ?? title.episode,
    flags: playbackOption.flags ?? title.flags,
    mergeReason: playbackOption.mergeReason ?? undefined,
    originalTitle: playbackOption.originalTitle ?? title.originalTitle,
    playbackOptions: [toDetailedPlaybackOption(playbackOption, summary)],
    price: price ?? title.price,
    productionYear: playbackOption.productionYear ?? title.productionYear,
    scoreRating: playbackOption.scoreRating ?? title.scoreRating,
    season: playbackOption.season ?? title.season,
    series: playbackOption.series ?? title.series,
    tags: getLocalizedValue(playbackOption.tags) ?? title.tags,
    type: playbackOption.titleType ?? title.type,
  };
};

const DetailPanel: FC<TTitleContext> = titleContext => {
  const { t } = useTranslation();
  const isNewIngest = useIsNewIngest();
  const { country, masterId, platformTitleId, playbackOptionId, providerIdentifier } = titleContext;
  const { subscriber: { vodPlatform } = {} } = useSubscriber();
  const { selectTitle } = useMetadataContext();

  const { setViewTitleAsSubscriber, viewTitleAsSubscriber, closeDetailPanel } =
    useMetadataContext();

  const adminTitleQueryResult = useTitleQuery({ country, masterId, platformTitleId });
  const { data: adminTitle } = adminTitleQueryResult;

  useEffect(() => {
    if (!masterId && adminTitle?.masterId) {
      selectTitle({ masterId: adminTitle.masterId, providerIdentifier });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTitle?.masterId]);

  const { playbackOptions = [] } = adminTitle || {};

  const isPlaybackOptionView = Boolean(playbackOptionId);
  const titleQueryResult = useTitleQuery(
    titleContext,
    { enabled: !isPlaybackOptionView },
    viewTitleAsSubscriber
  );
  const playbackOptionQueryResult = usePlaybackOptionQuery(
    playbackOptionId,
    undefined,
    viewTitleAsSubscriber,
    country
  );
  const queryResult = isPlaybackOptionView ? playbackOptionQueryResult : titleQueryResult;
  const detailedTitle = isPlaybackOptionView ? undefined : titleQueryResult.data;
  const playbackOption = isPlaybackOptionView ? playbackOptionQueryResult.data : undefined;
  const isError = queryResult.isError;

  const simpleTitle = useAppSelector(
    getSimpleTitle(masterId ?? platformTitleId ?? ''),
    shallowEqual
  );

  const title = detailedTitle || simpleTitle;
  const baseTitle = adminTitle || title;
  const selectedPlaybackOption = playbackOptions.find(({ id }) => id === playbackOptionId);
  const playbackOptionTitle = toDetailedTitle(adminTitle, playbackOption, selectedPlaybackOption);
  const availabilityPlaybackOptions = isPlaybackOptionView
    ? playbackOptionTitle?.playbackOptions ||
      (selectedPlaybackOption ? [selectedPlaybackOption] : [])
    : playbackOptions;
  const { availableFrom, availableTo } = getAvailabilityWindow(availabilityPlaybackOptions);
  const displayTitle = playbackOptionTitle || detailedTitle;
  const displayQueryResult = isPlaybackOptionView
    ? {
        ...playbackOptionQueryResult,
        data: playbackOptionTitle,
        error: playbackOptionQueryResult.error ?? adminTitleQueryResult.error,
        isLoading: playbackOptionQueryResult.isLoading || adminTitleQueryResult.isLoading,
      }
    : titleQueryResult;

  const [selectedTab, setSelectedTab] = useState<TabKey>(defaultTab);

  const tabBoxProps: BoxProps = {
    height: 1,
    overflow: 'auto',
    position: 'absolute',
    width: 1,
  };

  const tabWrapperProps: BoxProps = { flex: 1, position: 'relative' };

  const isHuaweiVOD = adminTitle?.playbackOptions.every(
    ({ sourceId }) => sourceId === SourceId.HUAWEI_VOD
  );
  const isCVP = adminTitle?.playbackOptions.every(({ sourceId }) => sourceId === SourceId.COMCAST);

  const noProvAccess =
    !!adminTitle &&
    viewTitleAsSubscriber &&
    isError &&
    ((isHuaweiVOD && vodPlatform === PlatformId.CVP) ||
      (isCVP && vodPlatform === PlatformId.Huawei));

  const { getProviderName } = useProviderNames();

  return (
    <StyledDrawer
      anchor='right'
      classes={{ paper: 'paper' }}
      onClose={closeDetailPanel}
      open
      slotProps={{ paper: { variant: 'elevation', sx: { overflowY: 'hidden !important' } } }}
    >
      <TitleBar direction='row' spacing={1}>
        <Tooltip title={t('close')}>
          <IconButton color='inherit' onClick={closeDetailPanel} size='small'>
            <Icon name='close' />
          </IconButton>
        </Tooltip>

        {baseTitle && (
          <HighlightedTypography
            asset={baseTitle}
            component='h2'
            field='titleName'
            noWrap
            variant='h3'
          />
        )}

        {availabilityPlaybackOptions.length > 0 && (
          <AvailabilityTag endTime={availableTo} minified startTime={availableFrom} />
        )}

        <Stack alignItems='center' direction='row' flex={1} justifyContent='flex-end' spacing={0.5}>
          <ViewAs
            setViewAsSubscriber={setViewTitleAsSubscriber}
            viewAsSubscriber={viewTitleAsSubscriber}
          />

          {masterId && playbackOptions.length > 0 && (
            <Menu key={JSON.stringify(titleContext)}>
              <Tooltip
                title={
                  <Trans
                    components={{ strong: <strong /> }}
                    i18nKey={t(
                      'provider.tooltip',
                      selectedPlaybackOption
                        ? { provider: getProviderName(selectedPlaybackOption.providerIdentifier) }
                        : { context: 'aggregated' }
                    )}
                  />
                }
              >
                <Menu.ChipButton
                  color={isPlaybackOptionView ? 'info' : 'success'}
                  icon={
                    selectedPlaybackOption ? (
                      <ProviderIcon
                        providerIdentifier={selectedPlaybackOption.providerIdentifier}
                        size='small'
                      />
                    ) : undefined
                  }
                  label={
                    selectedPlaybackOption
                      ? getProviderName(selectedPlaybackOption.providerIdentifier)
                      : t('aggregated')
                  }
                  size='small'
                  variant='outlined'
                />
              </Tooltip>

              <Menu.Content>
                <Menu.Item
                  divider
                  onClick={() =>
                    selectTitle({
                      masterId,
                      playbackOptionId: undefined,
                      providerIdentifier: undefined,
                    })
                  }
                  selected={!playbackOptionId}
                >
                  {t('aggregated')}
                </Menu.Item>

                {playbackOptions.map(playbackOption => (
                  <Menu.Item
                    icon={
                      <ProviderIcon
                        providerIdentifier={playbackOption.providerIdentifier}
                        size='medium'
                        variant='rounded'
                      />
                    }
                    key={playbackOption.id}
                    onClick={() =>
                      selectTitle({
                        masterId,
                        playbackOptionId: playbackOption.id,
                        providerIdentifier: playbackOption.providerIdentifier,
                      })
                    }
                    selected={playbackOption.id === playbackOptionId}
                    subheading={playbackOption.platformTitleId}
                  >
                    {getProviderName(playbackOption.providerIdentifier)}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu>
          )}

          <HasPermission>
            {isPlaybackOptionView && isNewIngest ? (
              <PlaybackOptionOptionsMenu
                playbackOption={playbackOptionTitle?.playbackOptions[0]}
                title={adminTitle}
              />
            ) : !isPlaybackOptionView ? (
              <TitleOptionsMenu title={detailedTitle} />
            ) : null}
          </HasPermission>
        </Stack>
      </TitleBar>

      {noProvAccess ? (
        <DialogContent>
          <Alert severity='info'>
            <Trans
              components={[
                <Typography
                  component='span'
                  display='inline'
                  fontStyle='italic'
                  fontWeight={500}
                  variant='inherit'
                />,
              ]}
              i18nKey={t('content.noAccess', {
                sourceId: isHuaweiVOD ? SourceId.HUAWEI_VOD : SourceId.COMCAST,
              })}
            />
          </Alert>
        </DialogContent>
      ) : (
        <GenericDataContextProvider
          context=''
          preserveContentDuringLoad
          queryResult={displayQueryResult as typeof titleQueryResult}
        >
          <ImageGroupDataProvider
            filenameBase={displayTitle?.titleName ?? ''}
            getImageUrl={
              displayTitle
                ? (_, imageType) =>
                    getTitleImageUrlFromProvider(displayTitle, imageType, providerIdentifier)
                : undefined
            }
            imageId={displayTitle?.masterId}
            queryKey={providerIdentifier}
          >
            <Tabs
              onTabChange={newTab => setSelectedTab(newTab as TabKey)}
              selectedTab={selectedTab}
              tabRowWrapperProps={{
                sx: {
                  [`& .${tabsClasses.indicator}`]: {
                    bgcolor: isPlaybackOptionView ? 'info.main' : 'text.primary',
                    height: 3,
                  },
                  background: theme => theme.palette.background.default,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                },
              }}
              tabs={[
                {
                  BoxProps: tabBoxProps,
                  content: <Details titleType={baseTitle?.type} />,
                  title: 'Details',
                  value: 'details',
                  wrapperProps: tabWrapperProps,
                },
                {
                  BoxProps: { ...tabBoxProps, p: 2 },
                  content: displayTitle ? <ImageTab title={displayTitle} /> : null,
                  title: 'Images',
                  value: 'images',
                  wrapperProps: tabWrapperProps,
                },
                ...(baseTitle?.type === 'series'
                  ? [
                      {
                        content: <Episodes />,
                        title: 'Episodes',
                        BoxProps: { ...tabBoxProps, p: 0 },
                        value: 'episodes',
                        wrapperProps: tabWrapperProps,
                      },
                    ]
                  : []),
                {
                  content: (
                    <Box display='flex' flex={1} flexDirection='column' overflow='hidden'>
                      <Box flex={1} overflow='hidden' position='relative'>
                        <TitleDataTree
                          data={playbackOption}
                          header={
                            <SectionHeading gutterBottom={false} variant='h4'>
                              {isPlaybackOptionView ? 'PlaybackOption' : t('aggregated')}
                            </SectionHeading>
                          }
                        />
                      </Box>
                    </Box>
                  ),
                  BoxProps: { display: 'flex', flex: 1, flexDirection: 'column', p: 0 },
                  title: 'AI Search',
                  value: 'dataTree',
                  wrapperProps: { display: 'flex', flex: 1, flexDirection: 'column' },
                },
                {
                  content: (
                    <Box display='flex' flex={1} flexDirection='column' overflow='hidden'>
                      <Box flex={1} overflow='hidden' position='relative'>
                        <SqlDataTree
                          enabled={selectedTab === 'sql'}
                          header={
                            <SectionHeading gutterBottom={false} variant='h4'>
                              {isPlaybackOptionView ? 'PlaybackOption' : t('aggregated')}
                            </SectionHeading>
                          }
                          masterId={masterId}
                          playbackOptionId={playbackOptionId}
                        />
                      </Box>
                    </Box>
                  ),
                  BoxProps: { display: 'flex', flex: 1, flexDirection: 'column', p: 0 },
                  title: 'SQL',
                  value: 'sql',
                  wrapperProps: {
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    position: 'relative',
                  },
                },
                {
                  BoxProps: tabBoxProps,
                  content: <Products />,
                  title: t('products'),
                  value: 'products',
                  wrapperProps: tabWrapperProps,
                },
                {
                  BoxProps: { ...tabBoxProps, p: 0 },
                  content: <RawMetadataTabContent playbackOptions={playbackOptions} />,
                  title: t('rawMetadata'),
                  value: 'revisions',
                  wrapperProps: tabWrapperProps,
                },
              ]}
              wrapperProps={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: 1,
              }}
            />
          </ImageGroupDataProvider>
        </GenericDataContextProvider>
      )}
    </StyledDrawer>
  );
};

export const LoadingDetailPanel: FC = () => {
  const { closeDetailPanel } = useMetadataContext();

  return (
    <StyledDrawer
      anchor='right'
      classes={{ paper: 'paper' }}
      onClose={closeDetailPanel}
      open
      slotProps={{
        paper: { variant: 'elevation', sx: { overflowY: 'hidden !important' } },
      }}
    >
      <Loading />
    </StyledDrawer>
  );
};

export default DetailPanel;
